import React, { useState } from 'react';
import { Container, Grid, Image, Item, Icon, Button, Popup, Segment, Label, Dropdown } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import narratorVoicesImage from '../../assets/shutterstock_2294366549-1024x1024.png';
import AaiAudioPlayerMin from '../../components/aaiAudioPlayerMin';
import { addToCart, removeFromCart, selectIsInCart } from '../../slices/cartSlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
// Mock data for voice providers with sample voice data
const voiceProviderArray = [
  {
    imgSrc: narratorVoicesImage,
    header: "Sarah Johnson",
    description: "Professional voice actor with a warm, friendly tone. Specializes in narration and character voices with a wide emotional range.",
    created_date: "03/15/2025",
    updated_date: "03/18/2025",
    voiceSnippet: {
      fileName: "manyHoursLater.mp3",
      uuid: "voice-sample-uuid-1",
      // s3Url: "https://aai-api.s3.us-west-1.amazonaws.com/aai-users/91355c8b-c056-4861-979c-dc8bb4a1e1bf/voice-samples/manyHoursLater.mp3",
      s3Url: "https://aai-api.s3.us-west-1.amazonaws.com/aai-core/3rd-party-voice-samples/manyHoursLater.mp3",
      // s3Url: "https://aai-api.s3.us-west-1.amazonaws.com/aai-core/utilityAudio/manyHoursLater.mp3"
      // const defaultAudio = "https://aai-api.s3.us-west-1.amazonaws.com/aai-core/utilityAudio/uninitializedCharacterVoice.wav";

    },
    emotion: "Happy",
    providerProfile: {
      age: 34,
      gender: "Female",
      accent: "American",
      languages: ["English", "Spanish"],
      specialties: ["Audiobooks", "Animation"]
    }
  },
  {
    imgSrc: narratorVoicesImage,
    header: "Michael Chen",
    description: "Versatile voice talent with a deep, resonant voice. Perfect for documentaries, commercials, and corporate narration.",
    created_date: "02/28/2025",
    updated_date: "03/10/2025",
    voiceSnippet: {
      fileName: "michael_chen_sample.mp3",
      uuid: "voice-sample-uuid-2",
      // s3Url: "path/to/sample2.mp3"
      s2Url: "https://aai-api.s3.us-west-1.amazonaws.com/aai-core/utilityAudio/uninitializedCharacterVoice.wav"
    },
    emotion: "Neutral",
    providerProfile: {
      age: 42,
      gender: "Male",
      accent: "British",
      languages: ["English", "Mandarin"],
      specialties: ["Documentaries", "Commercials"]
    }
  },
  {
    imgSrc: narratorVoicesImage,
    header: "Aisha Williams",
    description: "Energetic and expressive voice actor with a youthful sound. Great for animation, video games, and young adult content.",
    created_date: "03/05/2025",
    updated_date: "03/19/2025",
    voiceSnippet: {
      fileName: "aisha_williams_sample.mp3",
      uuid: "voice-sample-uuid-3",
      s3Url: "path/to/sample3.mp3"
    },
    emotion: "Excited",
    providerProfile: {
      age: 29,
      gender: "Female",
      accent: "American",
      languages: ["English"],
      specialties: ["Animation", "Video Games"]
    }
  },
  {
    imgSrc: narratorVoicesImage,
    header: "Robert Garcia",
    description: "Dramatic and powerful voice with excellent emotional range. Ideal for trailers, promos, and intense narrative content.",
    created_date: "03/01/2025",
    updated_date: "03/12/2025",
    voiceSnippet: {
      fileName: "robert_garcia_sample.mp3",
      uuid: "voice-sample-uuid-4",
      s3Url: "path/to/sample4.mp3"
    },
    emotion: "Dramatic",
    providerProfile: {
      age: 38,
      gender: "Male",
      accent: "American",
      languages: ["English", "Portuguese"],
      specialties: ["Trailers", "Promos"]
    }
  },
  {
    imgSrc: narratorVoicesImage,
    header: "Emma Thompson",
    description: "Soothing and calming voice perfect for meditation apps, audiobooks, and educational content.",
    created_date: "02/20/2025",
    updated_date: "03/15/2025",
    voiceSnippet: {
      fileName: "emma_thompson_sample.mp3",
      uuid: "voice-sample-uuid-5",
      s3Url: "path/to/sample5.mp3"
    },
    emotion: "Calm",
    providerProfile: {
      age: 45,
      gender: "Female",
      accent: "British",
      languages: ["English", "French"],
      specialties: ["Meditation", "Audiobooks"]
    }
  }
];

// Voice Actor List Item Component
const VoiceProviderListItem = (props) => {
  const { index, header, description, created_date, updated_date, imgSrc, voiceSnippet, emotion } = props;
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [autoPlayAudio, setAutoPlayAudio] = useState(false);
  const dispatch = useDispatch();

  // Generate a unique voiceId if one doesn't exist
  const voiceId = voiceSnippet.uuid || `voice-${uuidv4()}`;

  // Check if this voice is in the cart
  const isInCart = useSelector(selectIsInCart(voiceId));

  const handlePlaySample = () => {
    setAutoPlayAudio(true);
    setShowAudioPlayer(true);
  };

  const handlePlaybackEnded = () => {
    // Optional: You could reset the state when playback ends
    // setShowAudioPlayer(false);
    setAutoPlayAudio(false);
  };

  const handleAddToCart = () => {
    // Create cart item with all necessary data
    const cartItem = {
      voiceId,
      voiceName: `${header} - ${emotion}`,
      header,
      emotion,
      description,
      imgSrc,
      price: 29.99, // Mock price
      currency: "USD",
      licenseType: "Standard",
      features: ["Commercial Use", "Unlimited Projects", "1 Year Updates"],
      voiceSnippet
    };

    if (isInCart) {
      dispatch(removeFromCart(voiceId));
    } else {
      dispatch(addToCart(cartItem));
    }
  };

  return (
    <Item key={index.toString()}>
      <Item.Content>
        {/* Header and Description Section with Image */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
          <Image size='tiny' className='listItemImg' src={imgSrc} />
          <div style={{ marginLeft: '15px' }}>
            <Item.Header as='a' style={{ width: "100%" }}>
              {header}
            </Item.Header>
            <Item.Meta>Voice Actor</Item.Meta>
            <Item.Description>
              <p style={{ maxWidth: "600px" }}>{description}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <Label>
                  <Icon name='heart' />
                  Emotion: {emotion}
                </Label>
                <Button
                  size='tiny'
                  color={isInCart ? 'green' : 'grey'}
                  onClick={handleAddToCart}
                >
                  <Icon name={isInCart ? 'check' : 'cart plus'} />
                  {isInCart ? 'In Cart' : 'Add to Cart'}
                </Button>
              </div>

            </Item.Description>
            <Item.Extra>
              created: <span>{created_date}</span>
              <span className="listUpdatedText">updated: {updated_date}</span>
            </Item.Extra>
          </div>
        </div>

        {/* Audio Player Section */}
        <Segment basic className="listItemPlayerSegment">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              icon
              labelPosition="left"
              size='small'
              color='blue'
              onClick={handlePlaySample}
            >
              <Icon name='play' />
              Play Sample
            </Button>

            {/* Only show the audio player when showAudioPlayer is true */}
            {showAudioPlayer && (
              <div style={{
                marginLeft: '10px',
                width: '350px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <AaiAudioPlayerMin
                  requestType="CORE_DATA"
                  folderName="3rd-party-voice-samples"
                  fileName={voiceSnippet.fileName}
                  sample={{ voiceSnippet: voiceSnippet }}
                  showHeader={false}
                  autoPlay={autoPlayAudio}
                  onEnded={handlePlaybackEnded}
                  customStyles={{ padding: "0px" }}
                />
              </div>
            )}
          </div>
        </Segment>


      </Item.Content>
    </Item>
  );
};

// Main Component
const VoiceLibrarySelection = () => {
  const [filteredProviders, setFilteredProviders] = useState(voiceProviderArray);

  // Filter handlers
  const handleEmotionFilter = (e, { value }) => {
    if (value === 'all') {
      setFilteredProviders(voiceProviderArray);
    } else {
      setFilteredProviders(voiceProviderArray.filter(provider =>
        provider.emotion.toLowerCase() === value.toLowerCase()
      ));
    }
  };

  return (
    <>
      {/* Header Section */}
      <div className='listContainer' style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src={narratorVoicesImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Voice Library</span>
              <span className="blgListSubTitle">Available Voice Actors</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Filtering Options */}
      <div className='listContainer'>
        <Segment basic>
          <Dropdown
            className='listItemDropDn'
            placeholder='Filter by emotion'
            fluid
            selection
            options={[
              { key: 'all', text: 'All Emotions', value: 'all' },
              { key: 'happy', text: 'Happy', value: 'happy' },
              { key: 'neutral', text: 'Neutral', value: 'neutral' },
              { key: 'excited', text: 'Excited', value: 'excited' },
              { key: 'dramatic', text: 'Dramatic', value: 'dramatic' },
              { key: 'calm', text: 'Calm', value: 'calm' },
            ]}
            onChange={handleEmotionFilter}
          />
        </Segment>
      </div>

      {/* Voice Actor List */}
      <div className='listContainer'>
        <Grid style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}>
          <Grid.Row>
            <Grid.Column>
              <Grid columns={2}>
                {filteredProviders.map((provider, index) => (
                  <Grid.Column key={index}>
                    <VoiceProviderListItem
                      key={index}
                      index={index}
                      header={provider.header}
                      description={provider.description}
                      created_date={provider.created_date}
                      updated_date={provider.updated_date}
                      imgSrc={provider.imgSrc}
                      voiceSnippet={provider.voiceSnippet}
                      emotion={provider.emotion}
                    />
                  </Grid.Column>
                ))}
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </>
  );
};

export default VoiceLibrarySelection;
